import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import colorsDark from '../../assets/colors/dark';

const PlaybackDebugMonitor = ({ video, debugLog }) => {
  const { t } = useTranslation();

  const isGateway = video?.type === 'on_gateway';
  const isDvr = video?.type === 'on_dvr';
  const isS3Played = debugLog?.s3Link && debugLog?.s3Link?.status !== 'skipped';

  if (!isGateway && !isDvr && !isS3Played) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return colorsDark.$online;
      case 'error':
        return colorsDark.$errors;
      case 'warning':
      case 'triggered':
        return colorsDark.$warning;
      case 'pending':
      case 'loading':
        return '#028fd5';
      case 'skipped':
      default:
        return colorsDark.$offline;
    }
  };

  const getStatusIcon = (status) => {
    const color = getStatusColor(status);
    switch (status) {
      case 'success':
        return <CheckCircleFilled style={{ color, marginRight: 8 }} />;
      case 'error':
        return <CloseCircleFilled style={{ color, marginRight: 8 }} />;
      case 'warning':
      case 'triggered':
        return <ExclamationCircleFilled style={{ color, marginRight: 8 }} />;
      case 'pending':
      case 'loading':
        return <LoadingOutlined style={{ color, marginRight: 8 }} />;
      case 'skipped':
      default:
        return <MinusCircleOutlined style={{ color, marginRight: 8 }} />;
    }
  };

  const renderStepItem = (titleConfig, stepData) => {
    const { status, error } = stepData || { status: 'skipped' };
    const color = getStatusColor(status);
    const titleText =
      typeof titleConfig === 'string'
        ? titleConfig
        : titleConfig[status] || titleConfig.default || titleConfig.pending || '';

    return (
      <div style={{ marginBottom: 6, padding: '2px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(status)}
          <span
            style={{
              fontWeight: 600,
              color: status === 'skipped' ? colorsDark.$offline : color,
            }}
          >
            {t(titleText)}
          </span>
        </div>
        {status === 'error' && error && (
          <div
            style={{
              marginLeft: 24,
              fontSize: 12,
              color: colorsDark.$errors,
              marginTop: 2,
            }}
          >
            {t('Error')}: {error}
          </div>
        )}
      </div>
    );
  };

  if (isGateway) {
    const s1 = debugLog?.gatewayCheck || { status: 'success' };
    const s2 = debugLog?.gatewayStream || { status: 'pending' };
    const s3 = debugLog?.gatewayPlayback || { status: 'pending' };

    const showS2 = s1.status === 'success';
    const showS3 = showS2 && s2.status === 'success';

    return (
      <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
        {renderStepItem(
          {
            success: 'Step 1: Verified local gateway record',
            pending: 'Step 1: Checking gateway record...',
            error: 'Step 1: Gateway record verification failed',
          },
          s1,
        )}
        {showS2 &&
          renderStepItem(
            {
              pending: 'Step 2: Requesting gateway stream...',
              success: 'Step 2: Gateway stream link received',
              error: 'Step 2: Failed to request gateway stream',
            },
            s2,
          )}
        {showS3 &&
          renderStepItem(
            {
              pending: 'Step 3: Connecting to gateway stream...',
              success: 'Step 3: Gateway stream playing',
              error: 'Step 3: Gateway stream playback failed',
            },
            s3,
          )}
      </div>
    );
  }

  if (isDvr) {
    const s1 = debugLog?.dvrCheck || { status: 'success' };
    const s2 = debugLog?.dvrStream || { status: 'pending' };
    const s3 = debugLog?.dvrPlayback || { status: 'pending' };

    const showS2 = s1.status === 'success';
    const showS3 = showS2 && s2.status === 'success';

    return (
      <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
        {renderStepItem(
          {
            success: 'Step 1: Verified local DVR record',
            pending: 'Step 1: Checking DVR record...',
            error: 'Step 1: DVR record verification failed',
          },
          s1,
        )}
        {showS2 &&
          renderStepItem(
            {
              pending: 'Step 2: Requesting DVR stream...',
              success: 'Step 2: DVR stream link received',
              error: 'Step 2: Failed to request DVR stream',
            },
            s2,
          )}
        {showS3 &&
          renderStepItem(
            {
              pending: 'Step 3: Connecting to DVR stream...',
              success: 'Step 3: DVR stream playing',
              error: 'Step 3: DVR stream playback failed',
            },
            s3,
          )}
      </div>
    );
  }

  const s1 = debugLog?.s3Link || { status: 'skipped' };
  const s2 = debugLog?.s3Playback || { status: 'skipped' };
  const s3 = debugLog?.fallback || { status: 'skipped' };
  const s4 = debugLog?.gatewayStream || { status: 'skipped' };
  const s5 = debugLog?.gatewayPlayback || { status: 'skipped' };

  const ENABLE_S3_FALLBACK_DEBUG = false;
  const isFallbackTriggered = ENABLE_S3_FALLBACK_DEBUG && s3.status === 'triggered';
  const showS2 = s1.status === 'success';
  const isS3Success = showS2 && s2.status === 'success' && !isFallbackTriggered;

  return (
    <div className="debug-monitor-section" style={{ paddingTop: 4 }}>
      {renderStepItem(
        {
          pending: 'Step 1: Requesting S3 link...',
          success: 'Step 1: S3 link received',
          error: 'Step 1: Failed to get S3 link',
        },
        s1,
      )}

      {showS2 &&
        renderStepItem(
          {
            pending: 'Step 2: Loading S3 playback...',
            success: 'Step 2: S3 playback started',
            error: 'Step 2: S3 playback failed',
          },
          s2,
        )}

      {isFallbackTriggered && (
        <>
          {renderStepItem(
            'Step 3: Switching to gateway playback',
            { status: 'triggered' },
          )}

          {renderStepItem(
            {
              pending: 'Step 4: Requesting gateway stream...',
              success: 'Step 4: Gateway stream received',
              error: 'Step 4: Failed to get gateway stream',
            },
            s4,
          )}

          {s4.status === 'success' &&
            renderStepItem(
              {
                pending: 'Step 5: Connecting to gateway playback...',
                success: 'Step 5: Gateway playback started',
                error: 'Step 5: Gateway playback failed',
              },
              s5,
            )}
        </>
      )}

      {isS3Success &&
        renderStepItem('Step 3: Playback is ready', {
          status: 'success',
        })}
    </div>
  );
};

export default PlaybackDebugMonitor;
